import { useEffect, useRef } from 'react';

interface UseHoldHotkeyOptions {
  interval?: number;
  /** Hold duration before auto-repeat begins. A press released before this
   *  delay fires the callback exactly once (no repeat) — this is what stops a
   *  normal keypress from advancing twice. */
  initialDelay?: number;
  enabled?: boolean;
  allowInInputs?: boolean;
  targetWindow?: Window;
  /** Call preventDefault on the matching keydown (incl. OS key-repeat) so the
   *  browser doesn't also act on the key — e.g. arrows scrolling a focused
   *  scroll container. */
  preventDefault?: boolean;
}

export function useHoldHotkey(
  key: string,
  callback: () => void,
  options: UseHoldHotkeyOptions = {}
) {
  const {
    interval = 250,
    initialDelay = 400,
    enabled = true,
    allowInInputs = false,
    targetWindow = window,
    preventDefault = false,
  } = options;

  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !targetWindow) return () => {};

    const isValidTarget = (target: EventTarget | null) => {
      if (allowInInputs) return true;
      const el = target as HTMLElement | null;
      const tag = el?.tagName?.toLowerCase();
      return tag !== 'input' && tag !== 'textarea' && !el?.isContentEditable;
    };

    const start = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== key.toLowerCase()) return;
      if (!isValidTarget(e.target)) return;

      // Suppress the browser default (e.g. arrows scrolling a focused scroll
      // container) on every matching keydown — including OS key-repeats, which
      // bail out below before our own interval fires.
      if (preventDefault) e.preventDefault();

      if (e.repeat) return;
      if (intervalRef.current !== null || timeoutRef.current !== null) return;

      callbackRef.current(); // fire immediately

      // Only begin auto-repeat after an initial hold delay, so a quick tap
      // (released before initialDelay) fires exactly once.
      timeoutRef.current = targetWindow.setTimeout(() => {
        timeoutRef.current = null;
        intervalRef.current = targetWindow.setInterval(() => {
          callbackRef.current();
        }, interval);
      }, initialDelay);
    };

    const stop = (e?: KeyboardEvent) => {
      if (e && e.key.toLowerCase() !== key.toLowerCase()) return;

      if (timeoutRef.current !== null) {
        targetWindow.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (intervalRef.current !== null) {
        targetWindow.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleBlur = () => stop();

    targetWindow.addEventListener('keydown', start);
    targetWindow.addEventListener('keyup', stop);
    targetWindow.addEventListener('blur', handleBlur);

    return () => {
      targetWindow.removeEventListener('keydown', start);
      targetWindow.removeEventListener('keyup', stop);
      targetWindow.removeEventListener('blur', handleBlur);
      stop();
    };
  }, [
    key,
    interval,
    initialDelay,
    enabled,
    allowInInputs,
    targetWindow,
    preventDefault,
  ]);
}
